import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const ENGINE_LABEL = {
  openai: "ChatGPT",
  anthropic: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
  google_aio: "Google AIO",
};

const STATUS = {
  accurate: { label: "Accurate", color: "#10A37F", bg: "var(--aw-accent-soft)", icon: CheckCircle2 },
  outdated: { label: "Outdated", color: "#B97A2B", bg: "#FAEFD8", icon: Clock },
  wrong: { label: "Wrong", color: "#C84A2F", bg: "#FFEFEA", icon: AlertTriangle },
};

function Donut({ data, size = 160, strokeWidth = 28 }) {
  const total = data.positive + data.neutral + data.negative || 1;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const segs = [
    { value: data.positive, color: "var(--aw-accent)" },
    { value: data.neutral, color: "var(--aw-muted-2)" },
    { value: data.negative, color: "#C84A2F" },
  ];
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--aw-border)"
        strokeWidth={strokeWidth}
      />
      {segs.map((s, i) => {
        const len = (s.value / total) * circumference;
        const dashArray = `${len} ${circumference - len}`;
        const dashOffset = -offset;
        offset += len;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={s.color}
            strokeWidth={strokeWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dasharray 0.6s ease" }}
          />
        );
      })}
      <text
        x="50%"
        y="48%"
        textAnchor="middle"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 32,
          fill: "var(--aw-ink)",
        }}
      >
        {data.positive_pct}%
      </text>
      <text
        x="50%"
        y="62%"
        textAnchor="middle"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fill: "var(--aw-muted)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Positive
      </text>
    </svg>
  );
}

export default function SentimentPage() {
  const { current, projects, selectProject } = useProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!current) return;
    setLoading(true);
    api
      .get(`/projects/${current.project_id}/sentiment`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [current]);

  const claims = useMemo(() => {
    if (!data?.claims) return [];
    if (statusFilter === "all") return data.claims;
    return data.claims.filter((c) => c.status === statusFilter);
  }, [data, statusFilter]);

  if (!current) {
    return (
      <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
        <div className="p-8 text-sm" style={{ color: "var(--aw-muted)" }}>
          No project selected.
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div data-testid="sentiment-page">
        <div className="mb-8">
          <div className="eyebrow">Tone &amp; accuracy</div>
          <h1
            className="mt-2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 40,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Sentiment &amp; Accuracy
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 560 }}>
            How AI engines portray your brand — positive, neutral, negative —
            and every factual claim they make, scored as accurate, outdated, or
            wrong with the page to update.
          </p>
        </div>

        {data?.is_seed && (
          <div
            className="mb-6 px-4 py-3 rounded-[10px] text-xs flex items-center justify-between"
            style={{ background: "var(--aw-accent-soft)", color: "var(--aw-accent-ink)" }}
            data-testid="sentiment-seed-banner"
          >
            <span>
              Showing representative data. Run your first scan to populate live
              sentiment.
            </span>
            <a href="/dashboard/runs" className="underline">
              Run now →
            </a>
          </div>
        )}

        {loading ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            Loading…
          </div>
        ) : !data ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            No data.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-5 mb-8">
              {/* Donut card */}
              <div className="card-aw card-aw-lg" data-testid="sentiment-donut-card">
                <div className="eyebrow mb-4">Tone distribution</div>
                <div className="flex items-center gap-6">
                  <Donut data={data.donut} />
                  <div className="flex flex-col gap-2.5 text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: "var(--aw-accent)",
                        }}
                      />
                      <span style={{ color: "var(--aw-ink-2)" }}>Positive</span>
                      <span className="mono tnum" style={{ color: "var(--aw-muted)" }}>
                        {data.donut.positive} · {data.donut.positive_pct}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: "var(--aw-muted-2)",
                        }}
                      />
                      <span style={{ color: "var(--aw-ink-2)" }}>Neutral</span>
                      <span className="mono tnum" style={{ color: "var(--aw-muted)" }}>
                        {data.donut.neutral} · {data.donut.neutral_pct}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: "#C84A2F",
                        }}
                      />
                      <span style={{ color: "var(--aw-ink-2)" }}>Negative</span>
                      <span className="mono tnum" style={{ color: "var(--aw-muted)" }}>
                        {data.donut.negative} · {data.donut.negative_pct}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Per-engine bar */}
              <div className="card-aw card-aw-lg" data-testid="sentiment-engines-card">
                <div className="eyebrow mb-4">Per engine</div>
                {data.engines.length === 0 ? (
                  <div
                    className="text-sm py-8 text-center"
                    style={{ color: "var(--aw-muted)" }}
                  >
                    No engine breakdown yet.
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {data.engines.map((eng) => {
                      const total = eng.positive + eng.neutral + eng.negative || 1;
                      return (
                        <div
                          key={eng.engine}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "100px 1fr 80px",
                            gap: 10,
                            alignItems: "center",
                          }}
                          data-testid={`sentiment-engine-${eng.engine}`}
                        >
                          <div className="text-sm" style={{ color: "var(--aw-ink-2)" }}>
                            {ENGINE_LABEL[eng.engine] || eng.engine}
                          </div>
                          <div
                            style={{
                              height: 8,
                              borderRadius: 999,
                              background: "var(--aw-surface-2)",
                              display: "flex",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${(eng.positive / total) * 100}%`,
                                background: "var(--aw-accent)",
                              }}
                            />
                            <div
                              style={{
                                width: `${(eng.neutral / total) * 100}%`,
                                background: "var(--aw-muted-2)",
                              }}
                            />
                            <div
                              style={{
                                width: `${(eng.negative / total) * 100}%`,
                                background: "#C84A2F",
                              }}
                            />
                          </div>
                          <div
                            className="mono tnum text-right text-sm"
                            style={{ color: "var(--aw-muted)" }}
                          >
                            {total}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Factual claims */}
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="eyebrow">Factual claims</div>
                <h2
                  className="mt-2"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 28,
                    lineHeight: 1.1,
                  }}
                >
                  Every claim AI engines make about you.
                </h2>
              </div>
              <div className="flex items-center gap-2" data-testid="sentiment-claim-filters">
                <button
                  onClick={() => setStatusFilter("all")}
                  className={`pill ${statusFilter === "all" ? "pill-accent" : ""}`}
                  style={{ cursor: "pointer", height: 28 }}
                  data-testid="sentiment-filter-all"
                >
                  All · {data.claims.length}
                </button>
                {["accurate", "outdated", "wrong"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`pill ${statusFilter === s ? "pill-accent" : ""}`}
                    style={{ cursor: "pointer", height: 28 }}
                    data-testid={`sentiment-filter-${s}`}
                  >
                    {STATUS[s].label} · {data.claims.filter((c) => c.status === s).length}
                  </button>
                ))}
              </div>
            </div>

            <div className="card-aw" style={{ padding: 0 }} data-testid="sentiment-claims-table">
              {claims.length === 0 ? (
                <div
                  className="text-center text-sm py-10"
                  style={{ color: "var(--aw-muted)" }}
                  data-testid="sentiment-claims-empty"
                >
                  No claims match this filter.
                </div>
              ) : (
                claims.map((c, i) => {
                  const meta = STATUS[c.status] || STATUS.accurate;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={i}
                      style={{
                        padding: "16px 20px",
                        borderBottom: "1px solid var(--aw-border)",
                      }}
                      data-testid={`sentiment-claim-${i}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 999,
                            background: meta.bg,
                            color: meta.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={14} />
                        </div>
                        <div className="flex-1">
                          <div
                            className="text-sm"
                            style={{ color: "var(--aw-ink)", lineHeight: 1.55 }}
                          >
                            "{c.claim}"
                          </div>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span
                              className="pill"
                              style={{
                                background: meta.bg,
                                color: meta.color,
                                borderColor: "transparent",
                                fontSize: 11,
                                height: 22,
                              }}
                            >
                              {meta.label}
                            </span>
                            <span
                              className="mono"
                              style={{
                                fontSize: 11,
                                color: "var(--aw-muted)",
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                              }}
                            >
                              {ENGINE_LABEL[c.engine] || c.engine}
                            </span>
                            {c.prompt && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "var(--aw-muted)",
                                  fontStyle: "italic",
                                }}
                              >
                                · "{c.prompt}"
                              </span>
                            )}
                          </div>
                          {c.reason && (
                            <div
                              className="mt-2 text-xs"
                              style={{ color: "var(--aw-muted)" }}
                            >
                              {c.reason}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
